import React from "react";
import TransportationActions from "../../transportationActions/transportationActions";
import {RouteComponentProps, withRouter} from "react-router";
import {RouteParamsModel} from "../../../../common/models/routeParams";
import i18n from "../../../../i18n/i18n";
import Table from "../../../../common/components/shared/table/table";
import {PhotosModel} from "../../../models/photos";
import {DateUtils} from "../../../../helpers/dateUtils";
import TransportUtils from "../../../../helpers/transportUtils";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {Api} from "../../../../helpers/api";
import Loader from "../../../../common/components/shared/loader/loader";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import ViewPhotoPopup from "./viewPhotoPopup/viewPhotoPopup";
import {OrderService} from "../../../services/orderService";
import {IOrderStore} from "../../../stores/orderStore";
import {storesContext} from "../../../../common/stores/stores";

interface OrderPhotosState {
    orderId: string;
    photos: PhotosModel[] | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayPopup: boolean;
    selectedPhoto: PhotosModel | null;
}

class OrderPhotos extends React.Component <RouteComponentProps, OrderPhotosState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            orderId: "",
            photos: null,
            response: undefined,
            displayPopup: false,
            selectedPhoto: null
        };
    }

    async componentDidMount() {
        const params = this.props.match.params as RouteParamsModel;
        const orderId = params.id;

        await this.setState({orderId}, async () => {
            await this.getAllPhotos();
        });

    }

    public render(): JSX.Element {
        const orderStore = this.context.orderStore as IOrderStore;

        return (
            <div className="transportation-order-photos">
                <div className="content transportation-order-content">
                    <TransportationActions
                        orderId={this.state.orderId || ""}
                        element="order"
                        relationType={orderStore.relationType.value}
                    />
                    {this.renderContent()}
                </div>
            </div>
        );
    }

    private renderContent() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("transportation.order.photos.retrievalErrorSuffix")}
                />
            );
        } else if (this.state.response) {
            return this.renderPhotos();
        } else {
            return (
                <Loader/>
            );
        }
    }

    private renderPhotos(): JSX.Element {
        return (
            <div className="transportation-order-photos-sub-content">
                <div className="header" data-testid="header">
                    {i18n.t("transportation.order.photos.header", {number: this.state.orderId})}
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId={"order-photos-reload-btn"}
                    />
                </div>
                {this.state.photos && (
                    <>
                        {this.renderPhotoTable()}
                        {this.renderViewPhotoPopup()}
                    </>
                )}
            </div>
        );
    }

    private renderPhotoTable(): JSX.Element {
        const properties = ["sideType", "direction", "createdAt"];
        const headers = properties.map(key => i18n.t("transportation.order.photos.headers." + key));

        return (
            <div className="section">
                <div className="sub-header" data-testid="photos-header">
                    {i18n.t("transportation.order.photos.subHeader")}
                </div>
                <Table
                    headers={headers}
                    rows={TransportUtils.renderDamageOrPhotoRows("photos", this.state.photos!)}
                    properties={properties}
                    testId="photos"
                    renderCell={this.renderCell}
                    pageable={false}
                    currentPage={0}
                    numberOfElements={0}
                    actions={this.renderActions.bind(this)}
                    renderEmptyCell={true}
                />
            </div>
        );
    }

    private renderViewPhotoPopup() {
        return (
            <ViewPhotoPopup
                displayPopup={this.state.displayPopup}
                closePopup={this.closePopup.bind(this)}
                selectedPhoto={this.state.selectedPhoto}
            />
        );
    }

    private closePopup() {
        this.setState({
            displayPopup: false,
            selectedPhoto: null
        });
    }

    private renderCell(row: any, property: string): JSX.Element {
        const value = row[property];
        switch (property) {
            case "sideType":
                return TransportUtils.renderSideType("transportation.order.photos", value);
            case "createdAt":
                return <>{DateUtils.getFormattedTimeWithDate(value).replace("-", " ")}</>;
            case "direction":
                return TransportUtils.renderDirection(value);
            default:
                return value;
        }
    }

    private renderActions(element: PhotosModel): JSX.Element {
        if (element && element.sideType && element.direction && element.id && element.deviceId) {
            return (
                <span
                    className="view-photo-button"
                    data-testid={`view-photo-${element.sideType}-${element.direction}`}
                    onClick={() => {
                        this.setState({
                            displayPopup: true,
                            selectedPhoto: element
                        });
                    }}
                >
                    {i18n.t("transportation.order.photos.actions.viewPhoto")}
                </span>
            );
        } else return <></>;
    }

    private async getAllPhotos() {
        const response = await OrderService.getAllPhotos(this.state.orderId);

        this.setState({
            photos: Api.isSuccess(response) ? response?.data : null,
            response
        });
    }

    private async reload() {
        this.setState({photos: null});

        await this.getAllPhotos();
    }
}

export default withRouter(OrderPhotos);